import React, {
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
} from "react";
import { Button, Modal, ModalBody } from "reactstrap";
import {
  beforeRedirect$,
  refreshToken$,
  tokenExpiration$,
} from "services/subjects";
import { Container } from "react-bootstrap/cjs";
import styles from "./SessionExpirationModal.module.scss";
import { useMsal } from "@azure/msal-react";
import { merge, fromEvent, timer } from "rxjs";
import {
  filter,
  map,
  reduce,
  switchMap,
  takeUntil,
} from "rxjs/operators";
import { sendCustomEvent } from "common/utility";
import { AUDITEVENTTYPE } from "services/trackService";

const DEFAULT_SESSION_SECONDS = 60 * 60;
const TIME_TO_SHOW_MODAL_IN_SECONDS = 120;

const getRemainingSeconds = (expiresAt: number) =>
  Math.max(Math.ceil((expiresAt - Date.now()) / 1000), 0);

const formatCountdown = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};
export const SessionExpirationModal = memo(() => {
  const [timeToExpire, setTimeToExpire] = useState(DEFAULT_SESSION_SECONDS);
  const [modalIsVisible, setModalIsVisible] = useState(false);

  const intervalRef = useRef<number | null>(null);
  const { instance: msalInstance } = useMsal();

  const clearCountdown = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const logout = useCallback(() => {
    clearCountdown();
    setModalIsVisible(false);

    sendCustomEvent({
      eventType: AUDITEVENTTYPE.MemberPortalLogOut,
    });

    msalInstance.logoutRedirect();
  }, [clearCountdown, msalInstance]);

  const refreshAccessToken = useCallback(() => {
    clearCountdown();
    setModalIsVisible(false);

    beforeRedirect$.next(null);
    refreshToken$.next(null);
  }, [clearCountdown]);

  const startAboutToLogOutModal = useCallback(
    (expiresAt: number) => {
      clearCountdown();
      setModalIsVisible(true);

      const updateRemainingTime = () => {
        const remaining = getRemainingSeconds(expiresAt);
        setTimeToExpire(remaining);

        if (remaining <= 0) {
          logout();
        }
      };

      updateRemainingTime();
      intervalRef.current = window.setInterval(updateRemainingTime, 1000);
    },
    [clearCountdown, logout]
  );

  useEffect(() => {
    const subscription = tokenExpiration$
      .pipe(
        filter((secondsToExpire): secondsToExpire is number => {
          return Number.isFinite(secondsToExpire) && secondsToExpire > 0;
        }),
        switchMap((secondsToExpire) => {
          const expiresAt = Date.now() + secondsToExpire * 1000;
          const waitMs = Math.max(
            (secondsToExpire - TIME_TO_SHOW_MODAL_IN_SECONDS) * 1000,
            0
          );

          return merge(
            fromEvent(document, "click"),
            fromEvent(document, "keydown")
          ).pipe(
            takeUntil(timer(waitMs)),
            reduce(() => true, false),
            map((hadUserActivity) => ({
              hadUserActivity,
              expiresAt,
            }))
          );
        })
      )
      .subscribe(({ hadUserActivity, expiresAt }) => {
        if (hadUserActivity) {
          refreshAccessToken();
          return;
        }

        startAboutToLogOutModal(expiresAt);
      });

    return () => {
      clearCountdown();
      subscription.unsubscribe();
    };
  }, [clearCountdown, refreshAccessToken, startAboutToLogOutModal]);

  return (
    <Container className={styles["token-expiration-modal"]}>
      <Modal
        isOpen={modalIsVisible}
        centered={true}
        className={styles["token-expiration-modal-container"]}
      >
        <ModalBody className={styles["token-expiration-modal-body"]}>
          <div className={styles["token-expiration-body"]}>
            <div className={styles["token-expiration-body-title"]}>
              Your session is about to expire!
            </div>
            <div className={styles["token-expiration-body-timer"]}>
              You will be logged out in {formatCountdown(timeToExpire)}. All your
              progress will be lost and your message will be discarded.
            </div>
            <Button
              color="primary"
              onClick={refreshAccessToken}
              block
              className={styles["token-expiration-modal-btn"]}
            >
              Stay Logged In
            </Button>
            <Button
              outline
              color="primary"
              onClick={logout}
              block
              className={styles["token-expiration-modal-btn"]}
            >
              Log Out Now
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Container>
  );
});

export default SessionExpirationModal;
